import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { login, clearError } from '../../store/slices/authSlice';
import type { AppDispatch, RootState } from '../../store';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const LoginPage: React.FC = () => {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDemoAccounts, setShowDemoAccounts] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { isLoading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Clear errors on component unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailOrUsername || !password) {
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(login({ emailOrUsername, password })).unwrap();

      if (rememberMe) {
        localStorage.setItem('rememberLogin', emailOrUsername);
      } else {
        localStorage.removeItem('rememberLogin');
      }
    } catch (error) {
      // Error is handled by Redux slice
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load remembered email/username on mount
  useEffect(() => {
    const rememberedLogin = localStorage.getItem('rememberLogin');
    if (rememberedLogin) {
      setEmailOrUsername(rememberedLogin);
      setRememberMe(true);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-8">
          <Link 
            to="/"
            className="w-16 h-16 rounded-full patreon-primary-gradient flex items-center justify-center transform hover:scale-105 transition-transform duration-300" 
            style={{ 
              background: 'var(--patreon-btn-primary-bg)',
              boxShadow: 'var(--patreon-shadow-soft-lg)',
            }}
          >
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M15.75 9.525c0 3.107-2.463 5.625-5.5 5.625S4.75 12.632 4.75 9.525 7.213 3.9 10.25 3.9s5.5 2.518 5.5 5.625zM19.25 20.1H1.75v-1.5c0-2.9 2.35-5.25 5.25-5.25h6.5c2.9 0 5.25 2.35 5.25 5.25v1.5z"/>
            </svg>
          </Link>
        </div>
        <h2 
          className="text-center text-4xl font-bold text-gray-900 mb-3"
          style={{
            fontFamily: 'var(--patreon-font-secondary)',
            letterSpacing: 'var(--patreon-letter-spacing-tight)',
          }}
        >
          Welcome back
        </h2>
        <p className="text-center text-base text-gray-600">
          Don't have an account?{' '}
          <Link
            to="/register"
            className="font-semibold transition-all duration-200 hover:opacity-80"
            style={{ color: 'var(--patreon-orange)' }}
          >
            Sign up for free
          </Link>
        </p>
      </div>

      <div className="mt-12 sm:mx-auto sm:w-full sm:max-w-md">
        <div 
          className="bg-white py-10 px-6 sm:px-12"
          style={{
            borderRadius: 'var(--patreon-radius-2xl)',
            boxShadow: 'var(--patreon-shadow-soft-xl)',
          }}
        >
          <form className="space-y-7" onSubmit={handleSubmit}>
            {/* Email or Username Field */}
            <div>
              <label 
                htmlFor="emailOrUsername" 
                className="block text-sm font-semibold text-gray-900 mb-2"
                style={{ fontFamily: 'var(--patreon-font-primary)' }}
              >
                Email or username
              </label>
              <div>
                <input
                  id="emailOrUsername"
                  name="emailOrUsername"
                  type="text"
                  autoComplete="username"
                  required
                  value={emailOrUsername}
                  onChange={(e) => setEmailOrUsername(e.target.value)}
                  className="appearance-none block w-full px-4 py-3.5 border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 hover:border-gray-400"
                  style={{ 
                    borderRadius: 'var(--patreon-radius-lg)',
                    borderColor: 'var(--patreon-neutral-30)',
                    fontFamily: 'var(--patreon-font-primary)',
                  }}
                  onFocus={(e) => { 
                    e.target.style.borderColor = 'var(--patreon-orange)'; 
                    e.target.style.boxShadow = '0 0 0 4px rgba(255, 66, 77, 0.08)'; 
                  }}
                  onBlur={(e) => { 
                    e.target.style.borderColor = 'var(--patreon-neutral-30)'; 
                    e.target.style.boxShadow = ''; 
                  }}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label 
                htmlFor="password" 
                className="block text-sm font-semibold text-gray-900 mb-2"
                style={{ fontFamily: 'var(--patreon-font-primary)' }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-4 py-3.5 pr-12 border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 hover:border-gray-400"
                  style={{ 
                    borderRadius: 'var(--patreon-radius-lg)',
                    borderColor: 'var(--patreon-neutral-30)',
                    fontFamily: 'var(--patreon-font-primary)',
                  }}
                  onFocus={(e) => { 
                    e.target.style.borderColor = 'var(--patreon-orange)'; 
                    e.target.style.boxShadow = '0 0 0 4px rgba(255, 66, 77, 0.08)'; 
                  }}
                  onBlur={(e) => { 
                    e.target.style.borderColor = 'var(--patreon-neutral-30)'; 
                    e.target.style.boxShadow = ''; 
                  }}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <EyeIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded transition-all duration-200 cursor-pointer"
                  style={{ 
                    accentColor: 'var(--patreon-orange)',
                    borderColor: 'var(--patreon-neutral-40)',
                  }}
                  onFocus={(e) => { e.target.style.boxShadow = '0 0 0 4px rgba(255, 66, 77, 0.08)'; }}
                  onBlur={(e) => { e.target.style.boxShadow = ''; }}
                />
                <label htmlFor="remember-me" className="ml-3 block text-sm text-gray-700 font-medium cursor-pointer">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-semibold transition-all duration-200 hover:opacity-80"
                  style={{ color: 'var(--patreon-orange)' }}
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div 
                className="p-4"
                style={{
                  backgroundColor: 'rgba(220, 38, 38, 0.05)',
                  borderRadius: 'var(--patreon-radius-lg)',
                  border: '1px solid rgba(220, 38, 38, 0.2)',
                }}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5"
                      style={{ color: 'var(--patreon-error)' }}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium" style={{ color: 'var(--patreon-error)' }}>{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className="w-full flex justify-center items-center py-4 px-6 text-base font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 active:scale-[0.98]"
                style={{ 
                  background: 'var(--patreon-btn-primary-bg)', 
                  borderRadius: 'var(--patreon-radius-lg)',
                  boxShadow: 'var(--patreon-shadow-soft)',
                  fontFamily: 'var(--patreon-font-primary)',
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting && !isLoading) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = 'var(--patreon-shadow-soft-xl)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--patreon-shadow-soft)';
                }}
              >
                {isSubmitting || isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            {/* Social Login */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="ml-2">Google</span>
              </button>

              <button
                type="button"
                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="ml-2">GitHub</span>
              </button>
            </div>
          </div>
        </div>

        {/* Demo Accounts Section */}
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setShowDemoAccounts(!showDemoAccounts)}
            className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {showDemoAccounts ? 'Hide' : 'Show'} Demo Accounts
          </button>

          {showDemoAccounts && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">🔐 Demo Login Credentials</h3>
              <div className="space-y-3 text-xs">
                <div className="bg-white rounded p-3 border border-blue-100">
                  <div className="font-semibold text-gray-900 mb-1">👑 Admin Access</div>
                  <div className="text-gray-600 font-mono">admin@mxpatreon.local</div>
                  <div className="text-gray-400 text-[10px] mt-1">Password: password123</div>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailOrUsername('admin@mxpatreon.local');
                      setPassword('password123');
                    }}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-[10px] font-medium"
                  >
                    → Fill form
                  </button>
                </div>

                <div className="bg-white rounded p-3 border border-blue-100">
                  <div className="font-semibold text-gray-900 mb-1">🎨 Creator Account</div>
                  <div className="text-gray-600 font-mono">creator1@mxpatreon.local</div>
                  <div className="text-gray-400 text-[10px] mt-1">Password: password123</div>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailOrUsername('creator1@mxpatreon.local');
                      setPassword('password123');
                    }}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-[10px] font-medium"
                  >
                    → Fill form
                  </button>
                </div>

                <div className="bg-white rounded p-3 border border-blue-100">
                  <div className="font-semibold text-gray-900 mb-1">💰 Patron Account</div>
                  <div className="text-gray-600 font-mono">patron1@mxpatreon.local</div>
                  <div className="text-gray-400 text-[10px] mt-1">Password: password123</div>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailOrUsername('patron1@mxpatreon.local');
                      setPassword('password123');
                    }}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-[10px] font-medium"
                  >
                    → Fill form
                  </button>
                </div>

                <div className="bg-white rounded p-3 border border-blue-100">
                  <div className="font-semibold text-gray-900 mb-1">🛡️ Moderator</div>
                  <div className="text-gray-600 font-mono">mod1@mxpatreon.local</div>
                  <div className="text-gray-400 text-[10px] mt-1">Password: password123</div>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailOrUsername('mod1@mxpatreon.local');
                      setPassword('password123');
                    }}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-[10px] font-medium"
                  >
                    → Fill form
                  </button>
                </div>

                <div className="bg-white rounded p-3 border border-blue-100">
                  <div className="font-semibold text-gray-900 mb-1">💬 Support Staff</div>
                  <div className="text-gray-600 font-mono">support1@mxpatreon.local</div>
                  <div className="text-gray-400 text-[10px] mt-1">Password: password123</div>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailOrUsername('support1@mxpatreon.local');
                      setPassword('password123');
                    }}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-[10px] font-medium"
                  >
                    → Fill form
                  </button>
                </div>

                <div className="text-center pt-2 border-t border-blue-200">
                  <div className="text-gray-600 text-[11px]">
                    <span className="font-medium">More accounts available:</span>
                    <br />
                    creator2-10@mxpatreon.local • patron2-60@mxpatreon.local
                    <br />
                    <span className="text-gray-400">All use password: password123</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            By signing in, you agree to our{' '}
            <Link to="/terms" className="text-orange-600 hover:text-orange-500">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-orange-600 hover:text-orange-500">
              Privacy Policy
            </Link>
          </p>
          <div className="mt-4">
            <Link
              to="/"
              className="inline-flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900"
            >
              <span aria-hidden>←</span>
              <span className="ml-2">Back to home</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
